import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, RefreshCw, Filter } from 'lucide-react';
import { mockRuns } from '../mocks/automationData';
import { StatusPill } from '../components/StatusPill';
const styles = {
  card: 'bg-[var(--colors-background-card)] border border-[var(--colors-border-light)] rounded-lg shadow-[var(--shadows-sm)]',
  heading: 'text-[var(--colors-text-primary)] font-semibold',
  subtext: 'text-sm text-[var(--colors-text-tertiary)]',
  th: 'px-6 py-3 font-medium text-[var(--colors-text-secondary)]'
};
function JsonBlock({
  data,
  tone
}: {
  data: unknown;
  tone: 'in' | 'out' | 'err';
}) {
  const color = tone === 'err' ? 'text-red-400' : tone === 'out' ? 'text-blue-300' : 'text-green-400';
  return <pre className={`bg-gray-900 ${color} p-4 rounded-md font-mono text-xs overflow-x-auto`} data-id="element-2954">
      {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
    </pre>;
}
export function AutomationRuns() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const rows = mockRuns.filter(r => statusFilter === 'All' || r.status === statusFilter);
  return <div className="max-w-7xl mx-auto p-6 space-y-6" data-id="element-2955">
      <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-2956">
        <div className="flex items-center gap-4" data-id="element-2957">
          <button onClick={() => navigate('/automations')} className="p-1.5 rounded-md hover:bg-[var(--colors-neutral-100)] text-[var(--colors-text-tertiary)] transition-colors" data-id="element-2958">
            <ArrowLeft className="w-5 h-5" data-id="element-2959" />
          </button>
          <div data-id="element-2960">
            <h1 className={`text-2xl ${styles.heading}`} data-id="element-2961">Run history</h1>
            <p className={`${styles.subtext} mt-1`} data-id="element-2962">
              Execution logs and step-by-step results for all automations.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-2963">
          <Filter className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-2964" />
          <select className="border border-[var(--colors-border-default)] rounded-md text-sm py-1.5 pl-3 pr-8 bg-[var(--colors-background-card)] text-[var(--colors-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primaryring)]" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-2965">
            <option value="All" data-id="element-2966">All statuses</option>
            <option value="Success" data-id="element-2967">Success</option>
            <option value="Failed" data-id="element-2968">Failed</option>
            <option value="Filtered" data-id="element-2969">Filtered</option>
            <option value="Retrying" data-id="element-2970">Retrying</option>
          </select>
        </div>
      </div>

      <div className={styles.card} data-id="element-2971">
        <div className="overflow-x-auto" data-id="element-2972">
          <table className="w-full text-left text-sm text-[var(--colors-text-secondary)]" data-id="element-2973">
            <thead className="border-b border-[var(--colors-border-light)] bg-[var(--colors-neutral-50)]" data-id="element-2974">
              <tr data-id="element-2975">
                <th className="w-10 px-4 py-3" data-id="element-2976" />
                <th className={styles.th} data-id="element-2977">Timestamp</th>
                <th className={styles.th} data-id="element-2978">Automation</th>
                <th className={styles.th} data-id="element-2979">Trigger event ID</th>
                <th className={styles.th} data-id="element-2980">Status</th>
                <th className={styles.th} data-id="element-2981">Duration</th>
                <th className="px-6 py-3 font-medium text-[var(--colors-text-secondary)] text-right" data-id="element-2982">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2983">
              {rows.map(run => <Fragment key={run.id} data-id="element-2984">
                  <tr className="border-b border-[var(--colors-neutral-100)] hover:bg-[var(--colors-neutral-50)] cursor-pointer" onClick={() => setExpanded(expanded === run.id ? null : run.id)} data-id="element-2985">
                    <td className="px-4 py-4 text-center" data-id="element-2986">
                      {expanded === run.id ? <ChevronDown className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-2987" /> : <ChevronRight className="w-4 h-4 text-[var(--colors-text-tertiary)]" data-id="element-2988" />}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2989">
                      {new Date(run.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 font-medium text-[var(--colors-text-primary)]" data-id="element-2990">
                      {run.automationName}
                    </td>
                    <td className="px-6 py-4 font-mono text-xs" data-id="element-2991">
                      {run.triggerEventId}
                    </td>
                    <td className="px-6 py-4" data-id="element-2992">
                      <StatusPill status={run.status} data-id="element-2993" />
                    </td>
                    <td className="px-6 py-4 font-mono" data-id="element-2994">{run.durationMs}ms</td>
                    <td className="px-6 py-4 text-right" data-id="element-2995">
                      {run.status === 'Failed' && <button onClick={e => {
                    e.stopPropagation();
                  }} className="text-sm text-[var(--brand-primary)] hover:text-[var(--brand-primaryhover)] flex items-center gap-1 ml-auto" data-id="element-2996">
                          <RefreshCw className="w-3 h-3" data-id="element-2997" /> Retry
                        </button>}
                    </td>
                  </tr>
                  {expanded === run.id && <tr className="bg-[var(--colors-neutral-50)] border-b border-[var(--colors-border-light)]" data-id="element-2998">
                      <td colSpan={7} className="p-6" data-id="element-2999">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3000">
                          <div data-id="element-3001">
                            <h4 className="text-xs font-semibold text-[var(--colors-text-tertiary)] uppercase tracking-wider mb-2" data-id="element-3002">
                              Input payload
                            </h4>
                            <JsonBlock data={run.input} tone="in" data-id="element-3003" />
                          </div>
                          <div data-id="element-3004">
                            <h4 className="text-xs font-semibold text-[var(--colors-text-tertiary)] uppercase tracking-wider mb-2" data-id="element-3005">
                              {run.error ? 'Error' : 'Output payload'}
                            </h4>
                            <JsonBlock data={run.error ? run.error : run.output} tone={run.error ? 'err' : 'out'} data-id="element-3006" />
                          </div>
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}